// ============================================================
// OPERATIONAL VISION INSIGHTS — Fase 9.11
// Insights operacionais derivados de visão: paradas, riscos, atrasos
// ============================================================
import type { VisionEvent, PPEViolation, CameraDevice, VisionInsight } from '@/types/vision'

export interface VisionInsightContext {
  obraId:       string
  cameras:      CameraDevice[]
  events:       VisionEvent[]
  violations:   PPEViolation[]
  dronePending: boolean
}

// ─── Generate insights from vision data ──────────────────────
export function generateVisionInsights(ctx: VisionInsightContext): VisionInsight[] {
  const insights: VisionInsight[] = []
  const now = Date.now()

  // ── Insight 1: Inactive area ──────────────────────────────────
  const inactiveEvents = ctx.events.filter(
    e => e.tipo === 'inactivity_detected' && !e.is_resolved &&
         now - new Date(e.ts).getTime() < 7_200_000,
  )
  if (inactiveEvents.length >= 2) {
    insights.push({
      id:          `vi_inactive_${now}`,
      obra_id:     ctx.obraId,
      tipo:        'operational_gap',
      severity:    'warning',
      title:       '⚠ Área norte sem atividade operacional',
      description: `${inactiveEvents.length} câmera(s) registrando inatividade prolongada nas últimas 2h`,
      recommendation: 'Verificar status das equipes designadas para estas áreas e reagendar atividades se necessário',
      confidence:  0.78,
      evidence:    inactiveEvents.map(e => e.title),
      expires_at:  new Date(now + 3_600_000).toISOString(),
      created_at:  new Date().toISOString(),
    })
  }

  // ── Insight 2: Crane congestion ────────────────────────────────
  const congestionNearCrane = ctx.events.filter(
    e => e.tipo === 'congestion_detected' && !e.is_resolved,
  )
  if (congestionNearCrane.length > 0) {
    insights.push({
      id:          `vi_crane_${now}`,
      obra_id:     ctx.obraId,
      tipo:        'safety_risk',
      severity:    'critical',
      title:       '⚠ Concentração excessiva próximo à grua',
      description: 'Alta densidade de operários detectada em zona de risco',
      recommendation: 'Dispersar equipe imediatamente — manter distância mínima de segurança da grua',
      confidence:  0.82,
      evidence:    congestionNearCrane.map(e => e.detail ?? e.title),
      expires_at:  new Date(now + 1_800_000).toISOString(),
      created_at:  new Date().toISOString(),
    })
  }

  // ── Insight 3: Logistics blockage ─────────────────────────────
  const motionStops = ctx.events.filter(
    e => e.tipo === 'inactivity_detected' &&
         (e.metadata?.zone_tipo === 'entry_exit' || e.metadata?.zone_tipo === 'storage'),
  )
  if (motionStops.length > 0) {
    insights.push({
      id:          `vi_logistics_${now}`,
      obra_id:     ctx.obraId,
      tipo:        'logistics_blockage',
      severity:    'warning',
      title:       '⚠ Possível bloqueio logístico detectado',
      description: 'Inatividade em zona de entrada/saída ou armazenamento',
      recommendation: 'Verificar acesso de veículos e fluxo de materiais na obra',
      confidence:  0.65,
      evidence:    motionStops.map(e => e.title),
      expires_at:  new Date(now + 3_600_000).toISOString(),
      created_at:  new Date().toISOString(),
    })
  }

  // ── Insight 4: PPE systemic non-compliance ────────────────────
  const recentCriticalPPE = ctx.violations.filter(
    v => v.is_critical && now - new Date(v.ts).getTime() < 3_600_000,
  )
  if (recentCriticalPPE.length >= 3) {
    insights.push({
      id:          `vi_ppe_systemic_${now}`,
      obra_id:     ctx.obraId,
      tipo:        'ppe_systemic',
      severity:    'emergency',
      title:       '🚨 Descumprimento sistêmico de EPI',
      description: `${recentCriticalPPE.length} violações críticas de EPI na última hora`,
      recommendation: 'Paralisar obra para DDS emergencial. Notificar responsável SST imediatamente.',
      confidence:  0.91,
      evidence:    recentCriticalPPE.map(v => `EPI ausente: ${v.missing_ppe.join(', ')}`),
      expires_at:  new Date(now + 7_200_000).toISOString(),
      created_at:  new Date().toISOString(),
    })
  }

  // ── Insight 5: High offline camera rate ───────────────────────
  const offline = ctx.cameras.filter(c => c.status === 'offline').length
  const offlineRate = ctx.cameras.length > 0 ? offline / ctx.cameras.length : 0
  if (offlineRate > 0.4 && ctx.cameras.length >= 2) {
    insights.push({
      id:          `vi_cameras_${now}`,
      obra_id:     ctx.obraId,
      tipo:        'coverage_gap',
      severity:    'critical',
      title:       '📷 Cobertura visual crítica comprometida',
      description: `${offline} de ${ctx.cameras.length} câmeras offline`,
      recommendation: 'Restaurar câmeras offline imediatamente para manter monitoramento de segurança',
      confidence:  0.95,
      evidence:    ctx.cameras.filter(c => c.status === 'offline').map(c => `${c.nome}: offline`),
      expires_at:  new Date(now + 3_600_000).toISOString(),
      created_at:  new Date().toISOString(),
    })
  }

  // Sort by severity weight
  const SEVERITY_WEIGHT: Record<string, number> = { emergency: 4, critical: 3, warning: 2, info: 1 }
  return insights.sort((a, b) => (SEVERITY_WEIGHT[b.severity] ?? 0) - (SEVERITY_WEIGHT[a.severity] ?? 0))
}

// ─── Format insight for copilot ──────────────────────────────
export function formatInsightForCopilot(insight: VisionInsight): string {
  const icon = insight.severity === 'emergency' ? '🚨'
    : insight.severity === 'critical' ? '⚠'
    : insight.severity === 'warning'  ? '📋'
    : 'ℹ'
  return `${icon} ${insight.title}: ${insight.recommendation}`
}
